import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldValue,
  FieldValues,
} from 'features/libra/ontology/ontologyFieldTypes'
import { FormSectionSchema } from 'features/libra/sections/formSection/formSectionTypes'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import {
  ELIGIBILITY_STATUS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import pick from 'lodash/pick'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'

export const niiReportConfigSchema = z.object({
  sections: z.array(FormSectionSchema).optional(),
  fields: z.object({
    analysisDate: z.string(),
    executiveSummary: z.string(),
    internalComments: z.string(),
    eligibility: z.string(),
    eligibilityComputed: z.string(),
    recommendations: z.string(),
    justification: z.string().optional(),
    /** Ontology id for m², e.g. `inspectable_area_m2` */
    inspectableArea: z.string(),
    /** @deprecated Not used in ROI math; traditional cost is derived from group-level rates. Kept optional for backward compat. */
    traditionalInspectionCost: z.string().optional(),
  }),
  zoneFieldName: z.string().default('zones'),
})

export type NiiReportConfig = z.infer<typeof niiReportConfigSchema>
export type NiiReportFields = NiiReportConfig['fields']

export function useNiiLocalFieldValues(
  fields: NiiReportFields,
  defaults: FieldValues = {}
): {
  localValues: FieldValues
  setLocalFieldValue: (key: string, value: FieldValue) => void
  hasChanges: boolean
  doSave: () => Promise<void>
  resetChanges: () => void
  isLoading: boolean
  isSaving: boolean
} {
  const {
    fieldValues: ticketFieldValues,
    setFieldValues: saveToServer,
    isLoading,
  } = useTicketAccess()

  const managedKeys = useMemo(
    () =>
      Object.values(fields).filter((v): v is string => typeof v === 'string'),
    [fields]
  )

  const serverSnapshot = useMemo(
    () => pick(ticketFieldValues, managedKeys),
    [ticketFieldValues, managedKeys]
  )

  const [localValues, setLocalValues] = useState<FieldValues>(serverSnapshot)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setLocalValues(serverSnapshot)
  }, [serverSnapshot])

  const resolvedValues = useMemo(
    () => ({ ...defaults, ...localValues }),
    [defaults, localValues]
  )

  const hasChanges = useMemo(
    () => !isEqual(resolvedValues, serverSnapshot),
    [resolvedValues, serverSnapshot]
  )

  const setLocalFieldValue = useCallback((key: string, value: FieldValue) => {
    setLocalValues((prev) => ({ ...prev, [key]: value }))
  }, [])

  const doSave = useCallback(async () => {
    setIsSaving(true)
    try {
      await saveToServer(resolvedValues)
    } finally {
      setIsSaving(false)
    }
  }, [saveToServer, resolvedValues])

  const resetChanges = useCallback(() => {
    setLocalValues(serverSnapshot)
  }, [serverSnapshot])

  return {
    localValues: resolvedValues,
    setLocalFieldValue,
    hasChanges,
    doSave,
    resetChanges,
    isLoading,
    isSaving,
  }
}

export function getComputedEligibility(
  fieldValues: FieldValues,
  computedFieldKey: string
): EligibilityStatus {
  const computed = unwrapFieldValue<boolean>(fieldValues[computedFieldKey])
  return isNil(computed)
    ? ELIGIBILITY_STATUS.UNCLEAR
    : computed
      ? ELIGIBILITY_STATUS.ELIGIBLE
      : ELIGIBILITY_STATUS.NOT_ELIGIBLE
}
